<template>
  <Flex :align-items="'center'" :flex-direction="labelDirection()" :style="styles.container">
    <div v-if="labelSquare" class="square" />
    <label v-if="label" :class="getLabelClass()" :style="styles.label">
      {{ label }}
    </label>
    <template v-if="dataType === 'object'">
      <!-- eslint-disable-next-line vue/no-mutating-props -->
      <input v-if="type === 'text'" v-model="form[attr]" :style="styles.input" class="input" :type="type" />
      <input
        v-if="type === 'number'"
        v-model="displayNumber"
        :style="styles.input"
        class="input"
        :min="String(min)"
        :step="step"
        @change="handleChangeInput('form')"
        @focus="onFocus"
        @blur="onBlur"
      />
    </template>
    <template v-else-if="dataType === 'read-only-value'">
      <input v-if="type === 'text'" v-model="value" :style="styles.input" class="input" :type="type" readonly />
      <input v-if="type === 'number'" v-model.number="value" :step="step" :style="styles.input" class="input" :type="type" readonly />
    </template>
    <template v-else>
      <input v-if="type === 'text'" v-model="value" :style="styles.input" class="input" :type="type" @change="handleChangeInput" />
      <input
        v-if="type === 'number'"
        v-model.number="value"
        :step="step"
        :min="String(min)"
        :max="String(max)"
        :style="styles.input"
        class="input"
        :type="type"
        @change="handleChangeInput"
      />
    </template>
    <div v-if="innerInput" :style="styles.innerInput" class="inner-input-element">
      {{ innerInput }}
    </div>
  </Flex>
</template>

<script lang="ts">
import Flex from '@/components/atom/Flex.vue';
import { defineComponent, PropType, reactive, SetupContext, toRefs, computed, watchEffect, watch, ref } from 'vue';
import { CssProperties } from 'vue-css-definition';
import { TranslateResult } from 'vue-i18n';
import { splitNumberFormat } from '@/utils/display/numbers';
import { simpleCopy } from '@/utils/deepCopy';

interface IBaseField {
  value: string | number | undefined;
  formNumber: number;
}

export default defineComponent({
  name: 'BaseField',
  components: {
    Flex,
  },
  props: {
    type: {
      type: String,
      default: '',
    },
    form: {
      type: Object,
      require,
      default: () => {},
    },
    dataType: {
      type: String,
      default: 'object',
    },
    attr: {
      type: [String, Array] as PropType<string | string[]>,
      require,
    },
    label: {
      type: String as PropType<TranslateResult>,
    },
    labelTop: {
      type: Boolean,
      default: false,
    },
    labelFontSize: {
      type: String,
      default: undefined,
    },
    inputValue: {
      type: [String, Number] as PropType<string | number>,
    },
    labelSquare: {
      type: Boolean,
      default: false,
    },
    labelStyle: {
      type: Object as PropType<CssProperties>,
    },
    labelClass: {
      type: String,
      default: '',
    },
    innerInput: {
      type: String,
    },
    inputStyle: {
      type: Object as PropType<CssProperties>,
      defaylt: {},
    },
    textareaStyle: {
      type: Object as PropType<CssProperties>,
      defaylt: {},
    },
    innerInputPosition: {
      type: String,
      default: '5%',
    },
    step: {
      type: String,
      default: '1',
    },
    min: {
      type: Number,
    },
    max: {
      type: Number,
    },
  },
  setup(props, context: SetupContext) {
    const state: IBaseField = reactive<IBaseField>({
      value: 0,
      formNumber: 0,
    }) as IBaseField;
    // TODO: 以下のエラーを握りつぶすためにsimpleCopyで参照から値にキャストしてある。
    // 何か問題が起きそうだが一応今のところは問題なさそうなので。。。
    // Getting a value from the `props` in root scope of `setup()` will cause the value to lose reactivity.eslintvue/no-setup-props-destructure
    state.value = simpleCopy(props.inputValue);
    // console.log('checkaa', props.dataType, props.type);
    watch(
      () => props.inputValue,
      (val, preVal) => {
        state.value = val as unknown as string;
      }
    );
    watchEffect(() => {
      if (typeof props.form === 'object' && typeof props.attr == 'string' && props.form[props.attr] !== undefined) {
        state.formNumber = props.form[props.attr];
      }
    });
    const handleChangeInput = (type = 'value') => {
      let outputValue = state.value;
      if (type == 'form') {
        outputValue = state.formNumber;
      }
      context.emit('change', outputValue);
    };
    // if (typeof props.form === 'object' && typeof props.attr === 'string') {
    //       const number = props.form[props.attr].number
    //         if (number >= 0) {
    //           return number
    //         }
    //     }
    //     return null
    const setNumber = (value: number | string) => {
      if (Number(value) >= 0) {
        state.formNumber = Number(value);
        if (typeof props.form === 'object' && typeof props.attr == 'string') {
          // eslint-disable-next-line vue/no-mutating-props
          props.form[props.attr] = value;
        }
      } else {
        state.formNumber = 0;
      }
    };
    const displayNumber = computed({
      get: () => {
        // これがないと、入力値がnullの場合に0が表示される
        if (state.formNumber === null) {
          return 0;
        }

        // カンマ区切りには標準のtoLocaleString()メソッドを使用
        return isFocus.value ? state.formNumber : splitNumberFormat(state.formNumber) ?? 0;
      },
      set: (value: number | string) => setNumber(value),
    });

    const styles = {
      container: {
        width: '100%',
      },
      input: {
        width: props.label ? '64%' : '100%',
        ...props.inputStyle,
      },
      label: {
        width: props.labelTop ? 'auto' : '36%',
        fontSize: props.labelFontSize ?? '18px',
        marginBottom: props.labelTop ? '5px' : '',
        ...props.labelStyle,
      },
      innerInput: {
        right: props.innerInputPosition,
        // ↓でrelativeにして、その分marginを引いてあげないとスクロールについてこない
        position: 'relative',
        'margin-right': `-${props.innerInputPosition}`,
      },
      textarea: {
        width: props.label ? '80%' : '100%',
        ...props.textareaStyle,
      },
    };

    const labelDirection = () => {
      return props.labelTop ? 'column' : 'row';
    };
    const getLabelClass = (): string => {
      return props.labelClass ?? 'defaultLabel';
    };
    const isFocus = ref(false); // inputの制御のために必要

    const onFocus = () => {
      isFocus.value = true;
    };

    const onBlur = () => {
      isFocus.value = false;
    };

    return {
      ...toRefs(state),
      styles,
      labelDirection,
      getLabelClass,
      handleChangeInput,
      displayNumber,
      onFocus,
      onBlur,
    };
  },
});
</script>

<style lang="scss" scoped>
.input {
  font-size: 16px;
  /* height: 35px;
  position: relative; */
}
.inner-input-element {
  position: absolute;
  font-size: 18px;
  background: #eee;
  padding: 3px 10px;
  color: #333;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-weight: bold;
}
.square {
  height: 13px;
  width: 13px;
  background-color: gray;
  margin-right: 5px;
}
.require-label::after {
  width: 100%;
  content: ' *';
  color: red;
  vertical-align: middle;
}
// .defaultLabel {
//   content: ' *';
//   display: none;
// }
</style>
